import React, { useEffect, useState } from "react";
import "./StockDataAnalysis.css";
import { environment } from "environment/environment";
import useStockApiWebsocket from "service/websocket/StockApiWebsocket";

export default function StockDataAnalysis() {
    // WebSocket connection
    const { stockAnalysisData } = useStockApiWebsocket(
        environment.stockApiDayRangeDataWSUrl,
        ["stockAnalysisData"]
    );
    const [stockData, setStockData] = useState([]);

    /* ================= SORT STATE ================= */
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: "asc",
    });

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction:
                prev.key === key && prev.direction === "asc"
                    ? "desc"
                    : "asc",
        }));
    };
    /* ============================================== */

    useEffect(() => {
        if (Array.isArray(stockAnalysisData) && stockAnalysisData.length > 0) {
            setStockData(stockAnalysisData);
            console.log(stockAnalysisData);
        }
    }, [stockAnalysisData]);

    /* ================= HELPERS ================= */

    const getBullishAndBearishClass = (value) => {
        if (value == null) return "";
        return value >= 0 ? "bullish-color" : "bearish-color";
    };

    const getPdcOpendlClass = (value1, value2) => {
        if (value1 == null || value2 == null) return "";
        return Math.abs(Number(value1) - Number(value2)) <= 3
            ? "bg-yellow"
            : "";
    };

    const getGreenClass = (value1, value2) => {
        if (value1 == null || value2 == null) return "";
        return Math.abs(Number(value1) - Number(value2)) <= 3
            ? "bg-green"
            : "";
    };

    const getRedClass = (value1, value2) => {
        if (value1 == null || value2 == null) return "";
        return Math.abs(Number(value1) - Number(value2)) <= 3
            ? "bg-red"
            : "";
    };

    const getPdhPdlPdcDiff = (value1, value2) => {
        if (
            value1 == null ||
            value2 == null ||
            isNaN(value1) ||
            isNaN(value2)
        ) {
            return 0;
        }
        return Math.abs(Number(value1) - Number(value2));
    };

    /* ================= SORTED DATA ================= */
    const sortedStockData = React.useMemo(() => {
        if (!sortConfig.key) return stockAnalysisData;

        return [...stockAnalysisData].sort((a, b) => {
            let aVal = 0;
            let bVal = 0;

            switch (sortConfig.key) {
                case "PDH_PDC":
                    aVal = getPdhPdlPdcDiff(
                        a.previousDayHigh,
                        a.prev_close_price
                    );
                    bVal = getPdhPdlPdcDiff(
                        b.previousDayHigh,
                        b.prev_close_price
                    );
                    break;

                case "PDL_PDC":
                    aVal = getPdhPdlPdcDiff(
                        a.previousDayLow,
                        a.prev_close_price
                    );
                    bVal = getPdhPdlPdcDiff(
                        b.previousDayLow,
                        b.prev_close_price
                    );
                    break;

                case "PDC_OPEN":
                    aVal = getPdhPdlPdcDiff(
                        a.open_price,
                        a.prev_close_price
                    );
                    bVal = getPdhPdlPdcDiff(
                        b.open_price,
                        b.prev_close_price
                    );
                    break;

                case "DH_CLOSE":
                    aVal = getPdhPdlPdcDiff(a.high_price, a.lp);
                    bVal = getPdhPdlPdcDiff(b.high_price, b.lp);
                    break;

                case "DL_CLOSE":
                    aVal = getPdhPdlPdcDiff(a.low_price, a.lp);
                    bVal = getPdhPdlPdcDiff(b.low_price, b.lp);
                    break;

                /* ✅ CHG (%) SORTING — ADDED */
                case "CHG":
                    aVal = Number(a.chp) || 0;
                    bVal = Number(b.chp) || 0;
                    break;

                default:
                    return 0;
            }

            return sortConfig.direction === "asc"
                ? aVal - bVal
                : bVal - aVal;
        });
    }, [stockAnalysisData, sortConfig]);
    /* =============================================== */

    // Header Data
    const headerSource = stockData?.[0] ?? {};
    const headerData = [
        {
            label1: "Date",
            value1: headerSource?.localDate ?? "--",
            label2: "Time",
            value2: headerSource?.localTime ?? "--",
        },
    ];

    // Pivot Point = (High + Low + Previous Close) / 3
    const calculatePivotPoint = (high, low, prevClose) => {
        if (high == null || low == null || prevClose == null) return null;
        return (Number(high) + Number(low) + Number(prevClose)) / 3;
    };

    // Compare Pivot Point with Open Price
    const getPivotVsOpenLabel = (pivotPoint, openPrice) => {
        if (pivotPoint == null || openPrice == null) return null;
        if (pivotPoint > openPrice) return "P>O";
        if (pivotPoint < openPrice) return "P<O";
        return "P=O";
    };

    const isOpenEqualHigh = (open, high) => {
        return open != null && high != null && Number(open) === Number(high);
    };

    const isOpenEqualLow = (open, low) => {
        return open != null && low != null && Number(open) === Number(low);
    };

    const formatRangePercent = (value) => {
        if (value == null || value === "") return "--";
        const numericValue = Number(value);
        return Number.isFinite(numericValue) ? numericValue.toFixed(2) : "--";
    };


    return (
        <div className="stock-analysis-page">
            {/* ================= HEADER ================= */}
            <header className="header">
                <nav className="nav-container">

                    {/* LEFT SIDE - LOGO */}
                    <div className="left-nav">
                        <div className="logo-container">
                            <img
                                src="/assets/images/noptionwave-logo-1.png"
                                className="header-logo"
                                alt="noptionwave logo"
                            />
                        </div>
                    </div>

                    {/* RIGHT SIDE - HEADER DATA */}
                    <div className="right-nav">
                        {headerData.map((item, idx) => (
                            <div className="header-item" key={idx}>
                                <span className="header-line">
                                    {item.label1} : {item.value1}
                                </span>
                                <span className="header-line">
                                    {item.label2} : {item.value2}
                                </span>
                            </div>
                        ))}
                    </div>

                </nav>
            </header>

            <div className="table-container">
                <table className="responsive-table table-bordered text-center sticky-header stock-day-range-table">
                    <thead>
                        <tr>
                            <th>SYMBOL</th>
                            <th>15MH</th>
                            <th>15ML</th>
                            <th>15MH(%)</th>
                            <th>5MH</th>
                            <th>5ML</th>
                            <th>5MH(%)</th>
                            <th>LTP</th>
                            <th className="sortable" onClick={() => handleSort("CHG")}>
                                CHG (%)
                                {sortConfig.key === "CHG" ? sortConfig.direction === "asc" ? " ▲" : " ▼" : ""}
                            </th>
                            <th className="sortable" onClick={() => handleSort("PDC_OPEN")}>
                                PDC-OPEN {sortConfig.key === "PDC_OPEN" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                            </th>
                            <th>OPEN</th>
                            <th>PDH</th>
                            <th>PDL</th>
                            <th>PDC</th>

                            <th className="sortable" onClick={() => handleSort("PDH_PDC")}>
                                PDH-PDC {sortConfig.key === "PDH_PDC" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                            </th>

                            <th className="sortable" onClick={() => handleSort("PDL_PDC")}>
                                PDL-PDC {sortConfig.key === "PDL_PDC" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                            </th>

                            <th>DH</th>
                            <th>DL</th>
                            <th>CLOSE</th>

                            <th className="sortable" onClick={() => handleSort("DH_CLOSE")}>
                                DH-CLOSE {sortConfig.key === "DH_CLOSE" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                            </th>

                            <th className="sortable" onClick={() => handleSort("DL_CLOSE")}>
                                DL-CLOSE {sortConfig.key === "DL_CLOSE" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {sortedStockData?.length > 0 ? (
                            sortedStockData.map((row, idx) => (
                                <tr key={idx}>
                                    <td>{row.symbol?.split(":")[1]?.split("-")[0] ?? "--"}</td>
                                    <td>{row.firstFifteenMinuteHigh ?? "--"}</td>
                                    <td>{row.firstFifteenMinuteLow ?? "--"}</td>
                                    <td>{formatRangePercent(row.firstFifteenMinuteRangePercent)}</td>
                                    <td>{row.firstFiveMinuteHigh ?? "--"}</td>
                                    <td>{row.firstFiveMinuteLow ?? "--"}</td>
                                    <td>{formatRangePercent(row.firstFiveMinuteRangePercent)}</td>
                                    <td>{row.lp ?? "--"}</td>
                                    <td className={getBullishAndBearishClass(row.chp)}>
                                        {row.chp ?? "--"}
                                    </td>
                                    <td className={getPdcOpendlClass(row.open_price, row.prev_close_price)}>
                                        {getPdhPdlPdcDiff(row.open_price, row.prev_close_price).toFixed(2)}
                                    </td>
                                    <td>
                                        {/* Pivot Point vs Open */}
                                        <span className="pivot-flag">
                                            {(() => {
                                                const pivot = calculatePivotPoint(row.previousDayHigh, row.previousDayLow, row.prev_close_price);
                                                return getPivotVsOpenLabel(pivot, row.open_price) && ` ${getPivotVsOpenLabel(pivot, row.open_price)}`;
                                            })()}
                                        </span>
                                        {row.open_price ?? "--"}

                                        {/* O=H / O=L */}
                                        <span
                                            className={`ohl-flag ${isOpenEqualHigh(row.open_price, row.high_price) ? "oh-red" : ""}
                                                ${isOpenEqualLow(row.open_price, row.low_price) ? "ol-green" : ""}
                                            `}
                                        >
                                            {isOpenEqualHigh(row.open_price, row.high_price) && " O=H"}
                                            {isOpenEqualLow(row.open_price, row.low_price) && " O=L"}
                                        </span>


                                    </td>

                                    <td>{row.previousDayHigh ?? "--"}</td>
                                    <td>{row.previousDayLow ?? "--"}</td>
                                    <td>{row.prev_close_price ?? "--"}</td>

                                    <td className={getGreenClass(row.previousDayHigh, row.prev_close_price)}>
                                        {getPdhPdlPdcDiff(row.previousDayHigh, row.prev_close_price).toFixed(2)}
                                    </td>

                                    <td className={getRedClass(row.previousDayLow, row.prev_close_price)}>
                                        {getPdhPdlPdcDiff(row.previousDayLow, row.prev_close_price).toFixed(2)}
                                    </td>
                                    <td>{row.high_price ?? "--"}</td>
                                    <td>{row.low_price ?? "--"}</td>
                                    <td>{row.lp ?? "--"}</td>

                                    <td className={getGreenClass(row.lp, row.high_price)}>
                                        {getPdhPdlPdcDiff(row.high_price, row.lp).toFixed(2)}
                                    </td>

                                    <td className={getRedClass(row.lp, row.low_price)}>
                                        {getPdhPdlPdcDiff(row.low_price, row.lp).toFixed(2)}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="21">Waiting for live data...</td>
                            </tr>
                        )}
                    </tbody>

                </table>
            </div>

            <footer className="page-footer">noptionwave © 2026</footer>
        </div>
    );
}
